// Community comments section for the Academy.
//   • Public list (anyone can read)
//   • Logged-in users can post (FREE & PRO)
//   • Only admins can delete
// Visual language mirrors the rest of the Academy (dark theme, orange accents,
// rounded cards). i18n drives every visible string from comentarios-academia.js.
import React, { useCallback, useEffect, useState } from "react";
import { MessageCircle, Shield, Trash2, Crown } from "lucide-react";

import { API } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import {
  getComentariosI18n,
  tiempoRelativo,
  totalComentariosLabel,
  translateError,
} from "../../i18n/comentarios-academia";

// Deterministic per-user avatar background.
const avatarColor = (id = "") => {
  const palette = ["#00d4ff", "#f5a623", "#00e676", "#9b59ff", "#ff6b35", "#3498db"];
  const sum = id.split("").reduce((a, ch) => a + ch.charCodeAt(0), 0);
  return palette[sum % palette.length];
};

const Avatar = ({ id, name }) => (
  <div
    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-display text-sm font-black text-black"
    style={{ background: avatarColor(id) }}
    aria-hidden
  >
    {(name || "?").slice(0, 1).toUpperCase()}
  </div>
);

export const ComentariosAcademia = () => {
  const { user, token, openLogin, openRegister } = useAuth();
  const { lang } = useLanguage();
  const T = getComentariosI18n(lang);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const isAdmin = !!user?.is_admin;
  const charLeft = 500 - text.length;

  const authHeaders = useCallback(() => {
    const h = { "Content-Type": "application/json" };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [token]);

  const load = useCallback(
    async (p = 1, append = false) => {
      try {
        const res = await fetch(`${API}/academy/comments?page=${p}`, {
          credentials: "include",
        });
        const data = await res.json();
        setItems((prev) => (append ? [...prev, ...(data.comentarios || [])] : data.comentarios || []));
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
        setPage(p);
      } catch {
        // Network error — keep current state.
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    load(1);
  }, [load]);

  const publish = async () => {
    const t = text.trim();
    if (!t) {
      setError(T.errorVacio);
      return;
    }
    setPosting(true);
    setError("");
    try {
      const res = await fetch(`${API}/academy/comments`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders(),
        body: JSON.stringify({ text: t }),
      });
      const data = await res.json();
      if (!res.ok) {
        const code = typeof data?.detail === "string" ? data.detail : data?.detail?.msg || data?.error;
        setError(translateError(code, lang));
        return;
      }
      setItems((prev) => [data.comentario, ...prev]);
      setTotal((n) => n + 1);
      setText("");
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError(T.errorConexion);
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(T.confirmarBorrar)) return;
    try {
      const res = await fetch(`${API}/academy/comments/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: authHeaders(),
      });
      if (res.ok) {
        setItems((prev) => prev.filter((c) => c.id !== id));
        setTotal((n) => Math.max(0, n - 1));
      }
    } catch {
      // Ignore — UI stays in sync on next reload.
    }
  };

  return (
    <section
      className="mx-auto max-w-3xl px-4 py-10 md:px-10"
      data-testid="academy-comments"
    >
      <div className="mb-6">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-400/[0.08] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.3em] text-orange-300">
          <MessageCircle className="h-3 w-3" />
          {T.titulo}
        </div>
        <h2 className="font-display text-2xl font-black tracking-tight text-white md:text-3xl">
          {T.titulo}
        </h2>
        <p className="mt-1 text-sm text-white/55">
          {T.subtitulo}
          {total > 0 && <span className="text-white/40"> · {totalComentariosLabel(total, lang)}</span>}
        </p>
      </div>

      {/* Composer */}
      {user ? (
        <div className="mb-8 rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-xl">
          <div className="flex gap-3">
            <Avatar id={user.id || user.email || ""} name={user.name || user.email} />
            <div className="flex-1">
              <textarea
                value={text}
                onChange={(e) => {
                  setText(e.target.value.slice(0, 500));
                  setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) publish();
                }}
                placeholder={T.placeholder}
                rows={3}
                data-testid="academy-comment-input"
                className="w-full resize-y rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-orange-400/50 focus:bg-black/60"
              />
              <div className="mt-2 flex items-center justify-between gap-2">
                <span
                  className={`text-[10px] font-mono ${
                    charLeft < 20 ? "text-red-400" : charLeft < 50 ? "text-amber-300" : "text-white/35"
                  }`}
                >
                  {charLeft} {T.caracteresRestantes}
                </span>
                <button
                  onClick={publish}
                  disabled={posting || !text.trim()}
                  data-testid="academy-comment-submit"
                  className="inline-flex items-center gap-2 rounded-full border border-orange-400/60 bg-orange-500/15 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-orange-200 transition-all hover:bg-orange-500/25 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {posting ? T.publicando : T.botonPublicar}
                </button>
              </div>
              {error && (
                <p className="mt-2 text-[11px] font-bold text-red-300" data-testid="academy-comment-error">
                  {error}
                </p>
              )}
              {success && (
                <p className="mt-2 text-[11px] font-bold text-emerald-300" data-testid="academy-comment-success">
                  {T.publicado}
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div
          className="mb-8 flex flex-col items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl sm:flex-row sm:items-center sm:justify-between"
          data-testid="academy-comment-guest"
        >
          <p className="text-sm text-white/65">{T.sinCuenta}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openLogin}
              className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/70 transition hover:text-white"
              data-testid="academy-comment-login"
            >
              {T.loginBtn}
            </button>
            <button
              type="button"
              onClick={openRegister}
              className="inline-flex items-center gap-1 rounded-full border border-orange-400/60 bg-orange-500/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-orange-200 transition hover:bg-orange-500/25"
              data-testid="academy-comment-register"
            >
              {T.registroBtn}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="space-y-3" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-2xl border border-white/10 bg-white/[0.03]"
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center text-sm text-white/45">
          {T.sinComentarios}
        </div>
      ) : (
        <ul className="space-y-3" data-testid="academy-comments-list">
          {items.map((c) => (
            <li
              key={c.id}
              data-testid={`academy-comment-${c.id}`}
              className="group relative rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-xl"
            >
              <div className="flex gap-3">
                <Avatar id={c.user.id} name={c.user.name} />
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold text-white">{c.user.name}</span>
                    {c.user.isAdmin && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-cyan-400/30 bg-cyan-400/[0.08] px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-cyan-200">
                        <Shield className="h-2.5 w-2.5" />
                        {T.adminBadge}
                      </span>
                    )}
                    {c.user.plan === "PRO" && !c.user.isAdmin && (
                      <span className="rounded-full border border-orange-400/40 bg-orange-400/[0.08] px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-orange-200">
                        {T.proBadge}
                      </span>
                    )}
                    {c.user.isAmbassador && (
                      <Crown className="h-3 w-3 text-amber-300" aria-label="Ambassador" />
                    )}
                    <span className="ml-auto text-[10px] text-white/40">
                      {tiempoRelativo(c.createdAt, lang)}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-white/80">
                    {c.text}
                  </p>
                </div>
              </div>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => remove(c.id)}
                  data-testid={`academy-comment-delete-${c.id}`}
                  className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full border border-red-400/30 bg-red-400/[0.06] px-2 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-red-300 opacity-0 transition hover:bg-red-400/[0.12] group-hover:opacity-100"
                >
                  <Trash2 className="h-3 w-3" />
                  {T.borrar}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Load more */}
      {page < totalPages && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => load(page + 1, true)}
            data-testid="academy-comments-more"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/70 transition hover:text-white"
          >
            {T.verMas}
          </button>
        </div>
      )}
    </section>
  );
};

export default ComentariosAcademia;
